/**
 * MODULE: _shared/frontend-shell/src/components/assistant/emailTemplateSavedCoaching.ts
 * PURPOSE: In-thread assistant coaching after golden-path email template create/edit from assistant UX.
 * RECENT: 2026-06-06 [2.2.1328] — emailSendCoachingFingerprint for SSE retry dedupe on post-send coaching.
 */

import type { EmailTemplateSavedDetail } from '@shared/lib/frontend/utils/emailTemplateCreateBridge';

/** Coaching copy when save originated from assistant (not Sales Hub-only flows). */
export function buildEmailTemplateSavedCoachingMessage(
  detail: EmailTemplateSavedDetail
): string | null {
  if (!detail.coachInAssistant) return null;
  const title = (detail.templateTitle || '').trim();
  if (detail.mode === 'edit') {
    if (title) {
      return (
        `**${title}** is updated in your library. ` +
        `Try *send the ${title} template to [name]@company.com* or ask *list my email templates* to see usage rankings.`
      );
    }
    return (
      'Your email template is updated. Try *send the template to [name]@company.com* when you are ready to outreach.'
    );
  }
  if (title) {
    return (
      `**${title}** is saved. Welcome chips will refresh with your top templates — ` +
      `or try *send the ${title} template to [name]@company.com* now.`
    );
  }
  return (
    'Email template saved to your library. Ask *list my email templates* or use a welcome chip to send your first intro.'
  );
}

/** Stable key to suppress duplicate coaching bubbles on stream retries. */
export function emailSendCoachingFingerprint(payload: Record<string, unknown>): string | null {
  const templateId = String(payload.template_id || payload.templateId || '').trim();
  const templateTitle = String(
    payload.template_title || payload.templateTitle || payload.template_name || '',
  ).trim();
  const recipient = String(
    payload.recipient_email || payload.recipient || payload.to || '',
  ).trim();
  if (!templateId && !templateTitle) return null;
  return `${templateId || templateTitle}|${recipient}`.toLowerCase();
}

/** Coaching after a grounded template send completes (self-learning usage loop). */
export function buildEmailSendCoachingMessage(payload: Record<string, unknown>): string | null {
  const templateTitle = String(
    payload.template_title || payload.templateTitle || payload.template_name || ''
  ).trim();
  const templateId = String(payload.template_id || payload.templateId || '').trim();
  const recipient = String(
    payload.recipient_email || payload.recipient || payload.to || ''
  ).trim();
  if (!templateId && !templateTitle) return null;
  if (templateTitle && recipient) {
    return (
      `Sent **${templateTitle}** to ${recipient}. ` +
      `Brazn tracked this use — ask *list my email templates* to see updated rankings on your welcome chips.`
    );
  }
  if (templateTitle) {
    return (
      `**${templateTitle}** was sent. Template usage is tracked — your top templates will surface on welcome chips after context refreshes.`
    );
  }
  return 'Email sent with your template. Usage is tracked for smarter template suggestions.';
}
